import React from 'react';
import useSessionStore from '../../state/session';
import { useQuery } from '@tanstack/react-query';
import {
  fetchMySimpleInOutAvailability,
  fetchMySimpleInOutStatus,
  type SimpleInOutAvailabilityPayload,
} from '../../api/simpleInOut';

const STATUS_LABELS: Record<string, string> = {
  in: 'In',
  out: 'Out',
  remote: 'Remote',
  busy: 'Busy',
  break: 'Break',
  sick: 'Sick',
  vacation: 'Vacation',
};

const DISABLED_AVAILABILITY: SimpleInOutAvailabilityPayload = {
  ok: true,
  enabled: false,
  verified: false,
  verified_at: null,
  checked_at: null,
  token_source: 'none',
};

async function fetchAvailabilityQuietly(): Promise<SimpleInOutAvailabilityPayload> {
  try {
    return await fetchMySimpleInOutAvailability();
  } catch (error) {
    const status = (error as { status?: number } | null)?.status;
    if (status === 400 || status === 401 || status === 403 || status === 404 || status === 409 || status === 503) {
      return {
        ...DISABLED_AVAILABILITY,
        last_error: {
          status,
          message: error instanceof Error ? error.message : null,
        },
      };
    }
    throw error;
  }
}

export default function SimpleInOutHeaderStatus() {
  const level = useSessionStore(state => state.securityLevel);
  const staff = ['user','manager','admin','super_admin'].includes(level || '');
  const availabilityQuery = useQuery({
    queryKey: ['simple-inout', 'me', 'availability'],
    queryFn: fetchAvailabilityQuietly,
    enabled: staff,
    staleTime: 60_000,
    retry: false,
  });
  const enabled = availabilityQuery.data?.enabled === true;
  const statusQuery = useQuery({
    queryKey: ['simple-inout', 'me', 'status'],
    queryFn: fetchMySimpleInOutStatus,
    enabled: staff && enabled,
    refetchInterval: 60_000,
    retry: false,
  });

  if (!staff || availabilityQuery.isLoading || availabilityQuery.isError || !enabled) return null;

  const payload = statusQuery.data;
  const unavailable = statusQuery.isError || payload?.unavailable === true;
  if (!statusQuery.isLoading && !unavailable && (!payload || payload.mapped === false)) return null;

  const currentStatus = String(payload?.status?.status || '').toLowerCase();
  const label = statusQuery.isLoading
    ? 'Loading'
    : unavailable
      ? 'Unavailable'
      : STATUS_LABELS[currentStatus] || (currentStatus || 'Not set');

  return (
    <span
      className="simple-inout-status-portal-chip"
      aria-label={`Simple I/O status: ${label}`}
      title={`Simple I/O status: ${label}`}
    >
      <span className="simple-inout-status-pill" data-status={unavailable ? 'unknown' : currentStatus || 'unknown'}>
        <span className="simple-inout-status-pill__label">{label}</span>
      </span>
    </span>
  );
}
